import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { FileLinkWithMimeType, InboxContactChatMessage } from 'src/constants/types';
import {
  addMessage,
  appendMessages,
  setChat,
  setChatAsRead,
  setContacts,
  setLoadingMoreMessages,
  setMessageAsUnread,
} from '../slices';
import { useInboxApi } from '../api';

export const useGetInboxContactsService = () => {
  const dispatch = useDispatch();
  const { getContacts } = useInboxApi();

  return useCallback(
    async (companyId: string, query: string, offset: number, limit: number) => {
      return await getContacts({ companyId, query, offset, limit })
        .then(({ data }) => {
          dispatch(setContacts(data));
        })
        .catch(e => {
          console.log(e);
        });
    },
    [dispatch, getContacts],
  );
};

export const useGetInboxContactChatService = () => {
  const dispatch = useDispatch();
  const { getContactChat } = useInboxApi();

  return useCallback(
    async (
      companyId: string,
      contactId: string,
      offset: number,
      limit: number,
      appendToExisting = false,
      sendStatuses: 'pending' | 'non_pending' = 'non_pending',
    ) => {
      try {
        if (appendToExisting) {
          dispatch(setLoadingMoreMessages(true));
        }

        const { data } = await getContactChat({
          companyId,
          contactId,
          offset,
          limit,
          sendStatuses,
          ...(sendStatuses === 'pending' ? { sort: 'date_scheduled_asc' as const } : {}),
        });

        const sortedMessages = data.items.sort(
          (a: InboxContactChatMessage, b: InboxContactChatMessage) =>
            dayjs(a.dateCreated).isAfter(dayjs(b.dateCreated)) ? 1 : -1,
        );

        if (appendToExisting) {
          dispatch(appendMessages(sortedMessages));
        } else {
          dispatch(setChat({ ...data, items: sortedMessages }));
        }
      } catch (e) {
        console.log(e);
      } finally {
        if (appendToExisting) {
          dispatch(setLoadingMoreMessages(false));
        }
      }
    },
    [dispatch, getContactChat],
  );
};

export const useSetInboxChatAsReadService = () => {
  const dispatch = useDispatch();
  const { markChatAsRead } = useInboxApi();

  return useCallback(
    (companyId: string, contactIds: string[]) => {
      markChatAsRead({ contactIds }, companyId)
        .then(() => {
          dispatch(setChatAsRead(contactIds));
        })
        .catch(e => {
          console.log(e);
        });
    },
    [dispatch, markChatAsRead],
  );
};

export const useSetInboxMessageAsUnreadService = () => {
  const dispatch = useDispatch();
  const { markMessageAsUnread } = useInboxApi();

  return useCallback(
    (companyId: string, messageIds: string[]) => {
      markMessageAsUnread({ messageHistoryIds: messageIds }, companyId)
        .then(() => {
          dispatch(setMessageAsUnread(messageIds));
        })
        .catch(e => {
          console.log(e);
        });
    },
    [dispatch, markMessageAsUnread],
  );
};

export const useLoadMoreMessagesService = () => {
  const dispatch = useDispatch();
  const { getContactChat } = useInboxApi();

  return useCallback(
    async (
      companyId: string,
      contactId: string,
      offset: number,
      limit: number = 20,
      sendStatuses: 'pending' | 'non_pending' = 'non_pending',
    ) => {
      try {
        dispatch(setLoadingMoreMessages(true));

        const { data } = await getContactChat({
          companyId,
          contactId,
          offset,
          limit,
          sendStatuses,
        });

        const sortedMessages = data.items.sort(
          (a: InboxContactChatMessage, b: InboxContactChatMessage) =>
            dayjs(a.dateCreated).isAfter(dayjs(b.dateCreated)) ? 1 : -1,
        );

        dispatch(appendMessages(sortedMessages));
        return data;
      } catch (e) {
        console.log(e);
        throw e;
      } finally {
        dispatch(setLoadingMoreMessages(false));
      }
    },
    [dispatch, getContactChat],
  );
};

export const useAddInboxMessageService = () => {
  const dispatch = useDispatch();
  const { addMessage: addInboxMessage } = useInboxApi();

  return useCallback(
    (
      companyId: string,
      contactId: string,
      messageType: 'sms' | 'mms',
      text: string,
      links: string[],
      linksWithMimeType: FileLinkWithMimeType[],
    ) => {
      addInboxMessage({ contactId, messageType, text, links, linksWithMimeType }, companyId).then(
        () => {
          dispatch(addMessage({ contactId, messageType, text, links, linksWithMimeType }));
        },
      );
    },
    [dispatch, addInboxMessage],
  );
};
